// src/components/Onboarding.js
//
// First-run story. Four panels between opening the app and the sign-in
// screen, answering the question the login screen never did: why this app
// exists, and why an ultra plan is not a longer marathon plan.
//
// Deliberately NOT a feature tour. Each panel makes one claim, in the
// athlete's terms, with a drawing that carries the idea rather than a
// screenshot of a button.
//
// It names no data provider. Strava is the only live connector today and
// Garmin is already scaffolded; the pitch should not have to be rewritten
// when that changes, so the source is "your training history" and the
// provider is named at the connect step, after sign-in, where it is a
// fact rather than a promise.
//
// Shown once. The flag is written on finish AND on skip, in try/catch —
// a browser with storage blocked shows the story again rather than
// breaking, which is the right way round.

import React, { useEffect, useRef, useState } from "react";

export const ONBOARDED_KEY = "reset_onboarded_v1";

export function hasOnboarded() {
  try {
    return !!localStorage.getItem(ONBOARDED_KEY);
  } catch (_) {
    // Storage blocked (private mode, cleared site data). Treat as "not seen"
    // — showing the story twice is a smaller failure than never showing it.
    return false;
  }
}

/* ── Panel art ──────────────────────────────────────────────────────────
   Four small drawings, one idea each, in the app's own chart language:
   a stroke on a dark field, accent only where it carries meaning. They
   use currentColor so a panel can tint its own illustration. */

const ArtTerrain = () => (
  <svg viewBox="0 0 240 120" className="obArt" aria-hidden="true">
    {/* A marathon block is a ramp. An ultra is terrain. */}
    <path d="M8 96 L232 40" className="obArt__ghost" />
    <path
      d="M8 96 C36 92 44 62 60 58 C76 54 84 80 100 74 C118 67 124 28 142 30
         C158 32 160 66 176 62 C192 58 200 36 232 26"
      className="obArt__line"
    />
    <circle cx="142" cy="30" r="3.4" className="obArt__dot" />
  </svg>
);

const ArtFit = () => (
  <svg viewBox="0 0 240 120" className="obArt" aria-hidden="true">
    {/* Scattered weeks, and the plan drawn through them. */}
    {[
      [20, 84], [44, 70], [66, 78], [90, 58], [114, 64],
      [138, 44], [162, 52], [186, 34], [210, 40],
    ].map(([x, y], i) => (
      <circle key={i} cx={x} cy={y} r="3" className="obArt__point" />
    ))}
    <path
      d="M20 84 C48 76 58 70 90 62 C122 54 130 46 162 44 C186 42 196 38 212 34"
      className="obArt__line"
    />
  </svg>
);

const ArtAdapt = () => (
  <svg viewBox="0 0 240 120" className="obArt" aria-hidden="true">
    {/* The plan, a week that went wrong, and the corrected path. */}
    <path d="M8 78 C44 72 62 60 92 52" className="obArt__line" />
    <path d="M92 52 C112 48 122 86 142 92" className="obArt__miss" />
    <path d="M92 52 C124 46 140 34 232 24" className="obArt__ghost" />
    <path d="M142 92 C168 86 182 52 232 38" className="obArt__line obArt__line--dashed" />
    <circle cx="92" cy="52" r="3.4" className="obArt__dot" />
  </svg>
);

const ArtScore = () => (
  <svg viewBox="0 0 240 120" className="obArt" aria-hidden="true">
    {/* The ring, at rest. */}
    <circle cx="120" cy="60" r="42" className="obArt__track" />
    <circle
      cx="120" cy="60" r="42"
      className="obArt__ring"
      transform="rotate(-90 120 60)"
    />
    <text x="120" y="60" className="obArt__num" textAnchor="middle" dominantBaseline="central">77</text>
  </svg>
);

const PANELS = [
  {
    key: "terrain",
    art: ArtTerrain,
    title: "Ultra training isn’t more miles.",
    body: "Most plans scale a marathon block. Ultra is a different problem: time on feet, vertical, and arriving at the start line uninjured.",
  },
  {
    key: "fit",
    art: ArtFit,
    title: "One plan, built from your actual running.",
    body: "RESET reads your training history and builds a plan around what you really do — not a template you’ll abandon in week three.",
  },
  {
    key: "adapt",
    art: ArtAdapt,
    title: "It adapts when you don’t.",
    body: "Miss a week, run too hard, feel wrecked? The plan adjusts. You accept or decline every change — it never rewrites itself behind your back.",
  },
  {
    key: "score",
    art: ArtScore,
    title: "One number: are you on track?",
    body: "The RESET Score answers that every day, and shows its working — the five things it is built from, and which one to fix first.",
  },
];

export default function Onboarding({ onDone }) {
  const [i, setI] = useState(0);
  const trackRef = useRef(null);
  const dragRef = useRef(null);
  const last = PANELS.length - 1;

  const finish = () => {
    try {
      localStorage.setItem(ONBOARDED_KEY, "1");
    } catch (_) {
      /* storage blocked — the story simply shows again next time */
    }
    onDone();
  };

  const go = (n) => setI(Math.max(0, Math.min(last, n)));

  // Keyboard paging, so the story is not a touch-only surface.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") go(i + 1);
      else if (e.key === "ArrowLeft") go(i - 1);
      else if (e.key === "Escape") finish();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i]);

  /* Swipe. Pointer events rather than touch events, so a trackpad drag and
     a finger behave the same, and so it works on a desktop browser at all. */
  const onPointerDown = (e) => {
    dragRef.current = { x: e.clientX, t: Date.now(), moved: 0 };
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    d.moved = e.clientX - d.x;
    const el = trackRef.current;
    if (el) {
      // Rubber-band at the ends so the edges feel like edges.
      const over = (i === 0 && d.moved > 0) || (i === last && d.moved < 0);
      el.style.transition = "none";
      el.style.transform = `translate3d(calc(${-i * 100}% + ${d.moved * (over ? 0.32 : 1)}px), 0, 0)`;
    }
  };
  const endDrag = () => {
    const d = dragRef.current;
    dragRef.current = null;
    const el = trackRef.current;
    if (el) {
      el.style.transition = "";
      el.style.transform = "";
    }
    if (!d) return;
    // A short flick counts as much as a long drag.
    const fast = Date.now() - d.t < 300 && Math.abs(d.moved) > 40;
    if (d.moved < -60 || (fast && d.moved < 0)) go(i + 1);
    else if (d.moved > 60 || (fast && d.moved > 0)) go(i - 1);
  };

  return (
    <div className="obScreen" role="region" aria-label="What RESET Ultra does">
      <div className="obTop">
        <img src="/logo.svg" height="26" alt="RESET Ultra" className="obLogo" />
        <button type="button" className="obSkip" onClick={finish}>Skip</button>
      </div>

      <div
        className="obViewport"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onPointerLeave={endDrag}
      >
        <div
          className="obTrack"
          ref={trackRef}
          style={{ transform: `translate3d(${-i * 100}%, 0, 0)` }}
        >
          {PANELS.map((p, n) => {
            const Art = p.art;
            return (
              <section
                key={p.key}
                className={"obPanel" + (n === i ? " is-current" : "")}
                aria-hidden={n === i ? undefined : true}
              >
                <div className={`obArtWrap obArtWrap--${p.key}`}><Art /></div>
                <h2 className="obTitle">{p.title}</h2>
                <p className="obBody">{p.body}</p>
              </section>
            );
          })}
        </div>
      </div>

      <div className="obFoot">
        <div className="obDots" role="tablist" aria-label="Pages">
          {PANELS.map((p, n) => (
            <button
              key={p.key}
              type="button"
              role="tab"
              aria-selected={n === i}
              aria-label={`Page ${n + 1} of ${PANELS.length}`}
              className={"obDot" + (n === i ? " is-on" : "")}
              onClick={() => go(n)}
            />
          ))}
        </div>
        <button
          type="button"
          className="obNext"
          onClick={() => (i === last ? finish() : go(i + 1))}
        >
          {i === last ? "Get started" : "Continue"}
        </button>
      </div>
    </div>
  );
}
